import { NumberUtils } from "../utils/numberUtils";
import { ExternalCalibration, ICalibration } from "./calibration";
import { GenericFilter } from "./genericClass";
import { InternalEquipment } from "./internalEquipment";

export class CalibrationBalance implements ICalibration {
    id: number;

    internal = true;

    certificateNum: string;
    calibrationDate: Date;
    expirationDate: Date;
    showExpirationDate = true;

    projectNo: string;

    idStatus: number;
    statusTranslation: string;

    idEquipment: number;

    temperature: string;
    humidity: string;
    pressure: string;
    idPlace: number;

    regUser: string;
    modUser: string;

    regFc: Date;
    modFc: Date;

    realizationDate: Date;

    currentVersion: number;
    signedVersion: number;

    variables: CalibrationBalanceVariable[] = [];

    observations: string;
    correct: boolean;

    reason: string;
    actionType: number;
    executionNum: string;
    version: number;
    multichannel: boolean;
    channelNumber: number;
    idDocument: number;
    type: string;
}

export class CalibrationBalanceVariable {
    id: number;

    idProcedure: number;

    idVariable: number;
    variableTranslation: string;

    idUnit: number;
    unitName: string;

    extraField: number;
    idUnitExtra: number;

    idUnitInput: number;

    ambientConditions: CalibrationBalancesAmbientConditions;
    essays: CalibrationBalanceVariableEssay[] = [];
    configs: CalibrationBalanceVariableConfig[] = [];
    patterns: CalibrationBalancePattern[] = [];
    idsEssayType: number[];
}

export class CalibrationBalancePattern {
    id: number;
    idEquipment: number[];
    equipment: InternalEquipment[];

    values: CalibrationBalancePatternValue[];
    uuidConfig: string;
    uuidPattern: string;
    bMass: ExternalCalibration[];
}

export class CalibrationBalancePatternValue {
    id: number;

    value: number;
    idMass: number[];

    include: boolean;
    valid: boolean;
    uuid: string;
    uuidConfig: string;
    uncertainty: number;
    nominal: number;
    decimalPattern: number;
}

export class CalibrationBalancesAmbientConditions {
    id: number;
    idVariableCalibration: number;
    temperature: string;
    pressureUsed: string;
    oilUsed: string;
    idExcel: number;
    idParticlesExcel: number;
    serialNumber: string;
}

export class CalibrationBalanceVariableEssay {
    id: number;
    idCalibrationVariable: number;
    active: boolean = true;
    reasonActive: string;
    additionalInfo: string;
    idEssayType: number;
    regUser: string;
    modUser: string;
    regFc: Date;
    modFc: Date;
    uuidConfig: string;
    essayTranslation: string;
    essayLoad: number;
    essayPreload: number;
    essayLoadRead: number;
    patternValue: number;
    rangeScale: number = 0;
    toleratedPercentage: number = 0;
    asLeft: CalibrationBalanceEssayAsLeft[];
    asFound: CalibrationBalanceEssayAsFound[] = [];
    grossWeight: number;
    error: number;
}

export class CalibrationBalanceEssayAsFound {
    id: number;
    point: number;
    pattern: number;
    equipment: number;

    patternOriginal: string;
    equipmentOriginal: number;

    uuidConfig: string;
    uuidPattern: string;

    get correction(): number {
        if (this.pattern == null || this.equipment == null) {
            return null;
        }
        return NumberUtils.fixPrecision((+this.pattern) - this.equipment);
    }
}


export class CalibrationBalanceFilter extends GenericFilter {
    idStatus: number;
    idClient: number;
    regUser: string;

    name: string;
    type: string;

    maker: string;
    model: string;

    serialNum: string;
    certificateNum: string;

    calibrateDateStart: Date;
    calibrateDateEnd: Date;

    expirationDateStart: Date;
    expirationDateEnd: Date;

    realizationFcStart: Date;
    realizationFcEnd: Date;

    projectNo: string;

    idsVariable: number[];
    idsApp: number[];
    idPlace: number;

    cols: string[];
}

export enum CalibrationBalanceStatus {
    PENDIENTE_FIRMA = 1,
    FIRMADO = 2,
    EN_EJECUCION = 3,
    NO_VALIDO = 4
}

export class CalibrationBalanceVariableConfig {
    id: number;

    rangeInit: number;
    rangeEnd: number;

    resolution: number;
    tolerance: string;
    uncertaintyResolution: number;

    points: number[] = [];
    pointsLast: number[] = [];
    uuidConfig: string;
}

export class CalibrationBalanceAudit {
    id: number;
    username: string;

    idAction: number;
    actionName: string;

    idStatus: number;
    nameStatus: string;
    statusTranslation: string;

    date: Date;

    currentVersion: number;
    signedVersion: number;

    changes: string;

    canDownload: boolean;
}

export class CalibrationBalanceAuditFilter extends GenericFilter {
}

export class CalibrationBalanceEssayAsLeft {
    id: number;
    point: number;
    pattern: number;
    uncertaintyTypical: number;
    uuidPattern: string;

    average: number;
    correction: number;
    cPlusU: number;  // Incertidumbre corregida

    uRep: number; // Incertidumbre de repetibilidad
    uResol: number; // Incertidumbre por resolución
    uUnif: number; // Incertidumbre por falta de uniformidad
    uEstab: number; // Incertidumbre de estabilidad
    uC: number; // Incertidumbre típica
    uExc: number;
    neff: number; // Grados de libertad
    k: number; // Constante
    U: number; // Incertidumbre expandida
    spm: number;
    spmResulted: number;
    devesta: number;
    decimalPattern: number;
    uHister: number;
    spmCalculated: number;
    minumunResult: number;
    pMResult: number;
    uspCompliant: boolean;

    _values: CalibrationBalanceEssayAsLeftValue[];
    _patterns: number[];
    _resolution: number;

    idCalibrationConfig?: any;
    uuidConfig?: string;

    get patterns(): number[] {
        return this._patterns;
    }

    get resolution(): number {
        return this._resolution;
    }

    set resolution(resolution: number) {
        this._resolution = resolution;

        //this.recalculateValues();
    }

    set values(values: CalibrationBalanceEssayAsLeftValue[]) {
        this._values = values;

        //this.recalculateValues();
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    get values(): CalibrationBalanceEssayAsLeftValue[] {
        return this._values;
    }

    // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
    set patterns(patterns: number[]) {
        if (patterns) {
            this._patterns = patterns.map(p => p / 2);

            //this.recalculateValues();
        }
    }

    public initValues(): void {
        if (this._values == null && this.values != null) {
            this._values = this.values;
        }

        if (this._resolution == null && this.resolution != null) {
            this._resolution = this.resolution;
        }

        if (this._patterns == null && this.patterns != null) {
            this._patterns = this.patterns;
        }

        //this.recalculateValues();
    }

    public initData(resolution: number, values: CalibrationBalanceEssayAsLeftValue[], patterns: number[]): void {
        if (this._values == null && values != null) {
            this._values = values;
        }

        if (this._resolution == null && resolution != null) {
            this._resolution = resolution;
        }

        if (this._patterns == null && patterns != null) {
            this._patterns = patterns;
        }

       //this.recalculateValues();
    }

    
}

export class CalibrationBalanceEssayAsLeftValue {
    id: number;
    idAsLeft: number;
    value: number;
    idBalanceMass: number;
}

export class CalibrationBalanceVariableConfigValue {
    point: number;
    idBalanceMass: number;
}

export class CalibrationBalanceUncertaintiesValues {
    values: CalibrationBalancePatternValue[];
    idUnit: number;
    unit: string;
}

export enum CalibrationBalanceResponsibleEnum {
    INTERNAL = 1,
    EXTERNAL = 2
}

export class CalibrationBalanceVariablePoints {
    value: number;
    include: boolean;
    uuidConfig: string;
    uuidPattern: string;
}
